import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { PRICE_CHART_DEFAULT_OPTIONS } from "./lib/constants";
import { useAppSelector } from "../../store/hooks";
import { selectPriceChartRawData } from "../../features/productAnalyzer/productAnalyzer.selector";
import {
  filterChartRawDataByDateRange,
  formatChartRawDataToSeriesPoints,
  populateChartRawDataTillDatePoints,
} from "../../helpers";
import { Box } from "@mui/material";
import {
  selectGrowthMonthlyPlan,
  // selectIsFreePlan,
} from "../../features/pricingPlans/pricingPlans.selector";
import useProductAnalyzerParams from "../../hooks/useProductAnalyzerParams";
// import useChartOverlay from "../../hooks/useChartOverlay";

type TPriceChartProps = {
  fbmMinDateTimestamp?: number;
};

type TRef = {
  chart?: Highcharts.Chart | null;
};

export const PriceChart = forwardRef<TRef, TPriceChartProps>(
  ({ fbmMinDateTimestamp }, ref) => {
    // const { overlayRenderer } = useChartOverlay();

    const [options, setOptions] = useState<Highcharts.Options>(
      PRICE_CHART_DEFAULT_OPTIONS,
    );

    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const { priceChartRawData } = useAppSelector(selectPriceChartRawData);
    // const isFreePlan = useAppSelector(selectIsFreePlan);

    useImperativeHandle(
      ref,
      () => ({
        chart: chartRef.current?.chart,
      }),
      [],
    );

    useEffect(() => {
      if (!priceChartRawData.length) return;

      const [amazonRaw, fbaRaw, fbmRaw, buyBoxRaw, newRaw, salesRankRaw] =
        priceChartRawData;

      const filteredFbmRaw = fbmMinDateTimestamp
        ? filterChartRawDataByDateRange(
            fbmRaw,
            fbmMinDateTimestamp,
            new Date().getTime(),
          )
        : fbmRaw;

      const populatedAmazonRaw = populateChartRawDataTillDatePoints(
        amazonRaw,
        undefined,
        new Date().getTime(),
      );

      const series = formatChartRawDataToSeriesPoints(
        PRICE_CHART_DEFAULT_OPTIONS.series,
        [
          populatedAmazonRaw,
          fbaRaw,
          filteredFbmRaw,
          buyBoxRaw,
          newRaw,
          salesRankRaw,
        ],
      );

      const newOptions = {
        ...PRICE_CHART_DEFAULT_OPTIONS,
        series,
      };

      // if (isFreePlan) {
      //   newOptions.chart = {
      //     ...PRICE_CHART_DEFAULT_OPTIONS.chart,
      //     events: {
      //       render(this: Highcharts.Chart) {
      //         overlayRenderer({
      //           chart: this,
      //           title: "Rank & Price Chart",
      //         });
      //       },
      //     },
      //   };
      // }

      setOptions(newOptions);
    }, [priceChartRawData, fbmMinDateTimestamp]);

    if (
      !priceChartRawData.length ||
      (!priceChartRawData[0].length &&
        !priceChartRawData[1].length &&
        !priceChartRawData[2].length &&
        !priceChartRawData[3].length &&
        !priceChartRawData[4].length &&
        !priceChartRawData[5].length)
    )
      return null;

    return (
      <Box sx={{ position: "relative" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
        />
      </Box>
    );
  },
);

export default memo(PriceChart);
